import Vue from 'vue';
import { EventBus } from '../EventBus.js';

/**
 * #59 · **Mientras una resolución está en vuelo, esa solicitud no se puede volver a resolver.**
 *
 * Aprobar y denegar se confirman con un diálogo, y el diálogo se cierra **al aceptar**, no al
 * contestar el servidor. Entre una cosa y otra —una respuesta lenta son segundos— los botones de la
 * fila seguían habilitados, así que un segundo toque mandaba otra resolución con la **misma
 * `version`**, o directamente la contraria. El control optimista del servidor evita que las dos se
 * apliquen, pero una termina en conflicto y su aviso de error sale **después** del acuse de la que
 * sí fue: quien lo mira no sabe cuál ha valido (revisión CODEX del PR #1735, 17/10/2026).
 *
 * ## El cerrojo es de la SOLICITUD, no de la pantalla que lo cogió
 *
 * [17/10/2026, tanda del 10:04] Esto empezó siendo un contador en el `data` del componente, y así
 * escrito la revisión trajo **tres rondas seguidas de la misma familia**: el cerrojo duraba lo que
 * duraba la instancia, y lo que tiene que durar es lo que dura el POST.
 *
 *  - ronda 1 — el tramo entre aceptar el diálogo y la respuesta no estaba cubierto (lo cubría
 *    `cargando`, que empieza **después**, con la recarga);
 *  - ronda 2 — con `keepAlive`, cerrar sesión y entrar con otra cuenta conservaba la pantalla y su
 *    contador: un POST colgado de la cuenta anterior dejaba «Anular», «Aprobar» y «Denegar»
 *    apagados para quien entrara después;
 *  - ronda 3 — y al revés: la bandeja se **desmonta** al pasar a «Mis solicitudes»
 *    (`v-else-if` de `mis-vacaciones.vue`), así que volver a Pendientes creaba otra instancia con
 *    el contador a cero y los botones encendidos sobre la solicitud que seguía resolviéndose.
 *
 * Tapar los tres sitios por separado no cierra nada: el cerrojo pertenece a **la operación**, que
 * dura lo que dura la petición, y no a quien la mira. Así que vive aquí, en el módulo, con su
 * clave: la de la solicitud que se está resolviendo. Una instancia nueva nace viendo lo que ya está
 * puesto, una instancia destruida no suelta nada, y las tres pantallas que resuelven la misma
 * solicitud —la bandeja, el día amarillo del calendario y «Anular» de «Mis vacaciones»— comparten
 * un único cerrojo porque comparten la clave ({@link claveDeSolicitud}).
 *
 * Es el mismo planteamiento, y por el mismo motivo, que el registro de exportaciones de
 * «Facturación · control diario» (#173, decisión técnica del 07/10/2026): registro de módulo,
 * `Vue.observable` para que las instancias que se recrean no solo lo lean sino que **reaccionen**,
 * y un solo sitio que lo suelta.
 *
 * ## Quién lo coge, quién lo suelta y quién no puede soltarlo
 *
 * Lo coge y lo suelta {@link marcarEnVuelo} desde `accionDeLaSesion`, que es por donde pasan TODAS
 * las acciones del módulo: una acción nueva nace con su cerrojo sin que ningún llamante tenga que
 * acordarse, y ahí mismo está la guarda que **no deja abrir ni aceptar un segundo diálogo** sobre
 * una solicitud que ya se está resolviendo. Apagar el botón es la mitad visible; la garantía es esa.
 *
 * Se suelta por **testigo**, como las esperas (`esperaGlobal`): soltar uno que ya no está vivo
 * —porque lo tiró el cambio de cuenta— no cuenta, así que la respuesta que llega tarde no abre el
 * cerrojo de una acción nueva.
 *
 * ## El cambio de cuenta lo suelta; salir de la pantalla no
 *
 * `app:session:close` es salir y `app:session:open` es que ha entrado otra cuenta: los dos tiran
 * **todos** los cerrojos, porque la respuesta del POST de la cuenta anterior puede no llegar nunca
 * y quien entra no tiene forma de abrirlo (ronda 2).
 *
 * Salir de la pantalla, en cambio, **no lo suelta**, y es a propósito: la sesión es la misma, quien
 * pulsó sigue siendo quien va a recibir la respuesta y el POST sigue vivo. Encender ahí los botones
 * es dejar mandar otra vez la misma resolución —con la misma `version`, o la contraria—, que es
 * exactamente el fallo que este cerrojo vino a cerrar. Es la misma decisión que ya está tomada para
 * `enviando` en el formulario de solicitud (PR #1733, ronda 6): un cerrojo de envío no se suelta
 * desde fuera mientras el envío vive, porque encender un botón que al pulsarlo acaba en error es lo
 * que prohíbe §9 del `CLAUDE.md` del orquestador.
 *
 * Lo que sí se suelta al salir es la **espera** —el indicador es un modal de la aplicación y tapa
 * la pantalla de destino—, y vive donde tiene que vivir: en `preloaderDeLaSesion`, apuntada a las
 * tres salidas de `soloMandaLaDeDelante`.
 *
 * ## Cómo se usa
 *
 *     import { unaAccionALaVez } from './../../js/utils/unaAccionALaVez.js';
 *     mixins: [unaAccionALaVez],
 *
 * y en la plantilla, sobre los controles que resuelven esa solicitud:
 *
 *     :disabled="accionEnVueloSobre(claveDeSolicitud(fila.solicitud.id))"
 *
 * Un control apagado **dice por qué** (§9): junto a él va la frase de «se está resolviendo», que es
 * lo que explica el botón apagado al volver a la pestaña con el POST todavía en vuelo.
 */

/**
 * **Los cerrojos puestos, por clave.** Del módulo y no de ninguna instancia: ver arriba. Es
 * `Vue.observable` para que lo que se pinta con él reaccione sin que la pantalla pregunte.
 */
const cerrojos = Vue.observable({ sobre: {} });

/**
 * Los testigos vivos. Un `Set` y no la cuenta de `cerrojos` para poder saber si **este** sigue
 * contando: es lo que hace que soltar lo que ya se tiró no le quite el cerrojo a una acción nueva.
 */
const vivos = new Set();

/**
 * La clave de las acciones que no dicen sobre qué son. No debería hacer falta —`accionDeLaSesion`
 * lo avisa por consola—, y es a propósito la MISMA para todas: un cerrojo de más apaga un botón que
 * se volverá a encender al contestar el servidor, y uno de menos deja mandar la acción dos veces.
 */
export const SIN_IDENTIFICAR = 'accion:sin-identificar';

/**
 * **La clave de una acción sobre una solicitud de vacaciones, escrita en un solo sitio.**
 *
 * Aprobar, denegar y anular la misma solicitud son la misma operación para esto, se pulsen desde la
 * bandeja, desde el día amarillo del calendario o desde «Mis vacaciones»: si cada pantalla se
 * inventara su clave, el cerrojo de una no diría nada de la otra.
 */
export function claveDeSolicitud(id) {
  return 'vacaciones:solicitud:' + (id === null || id === undefined ? '?' : id);
}

/** Coge el cerrojo de esa clave y devuelve **el testigo**, que es lo único con lo que se suelta. */
export function cogerCerrojo(clave) {
  const testigo = { clave: clave || SIN_IDENTIFICAR };
  vivos.add(testigo);
  Vue.set(cerrojos.sobre, testigo.clave, (cerrojos.sobre[testigo.clave] || 0) + 1);
  return testigo;
}

/**
 * Suelta **ese** cerrojo. Devuelve si el testigo seguía vivo, que es lo que distingue «he soltado
 * lo mío» de «lo mío ya no estaba»: la respuesta de una acción de la cuenta anterior llega aquí
 * igual y no tiene que abrir nada.
 */
export function soltarCerrojo(testigo) {
  if (!testigo || !vivos.delete(testigo)) return false;
  const quedan = (cerrojos.sobre[testigo.clave] || 0) - 1;
  if (quedan > 0) Vue.set(cerrojos.sobre, testigo.clave, quedan);
  else Vue.delete(cerrojos.sobre, testigo.clave);
  return true;
}

/** Si hay alguna acción en vuelo sobre eso. Es lo que apaga los controles que la lanzan. */
export function cerrojoPuesto(clave) {
  return (cerrojos.sobre[clave || SIN_IDENTIFICAR] || 0) > 0;
}

/** Cuántas claves tienen cerrojo. Para quien tenga que decidir con eso, y para las pruebas. */
export function cerrojosEnVuelo() {
  return Object.keys(cerrojos.sobre).length;
}

/**
 * Los tira todos. Lo llama el cambio de cuenta y nadie más: una pantalla que se va no termina
 * ninguna petición, así que no tiene nada que soltar (ver arriba).
 */
function soltarTodos() {
  if (!vivos.size && !cerrojosEnVuelo()) return;
  vivos.clear();
  Object.keys(cerrojos.sobre).forEach(function(clave) {
    Vue.delete(cerrojos.sobre, clave);
  });
}

EventBus.$on('app:session:close', soltarTodos);
EventBus.$on('app:session:open', soltarTodos);

/**
 * Lo que la pantalla necesita para apagar sus controles. **No guarda estado**: lee el registro del
 * módulo, así que una instancia recién creada ve lo que ya hay puesto —que es lo que la ronda 3
 * encontró— y una destruida no se lleva nada.
 */
export const unaAccionALaVez = {
  computed: {
    /**
     * Si hay **alguna** acción del módulo en vuelo. Para un control que no es de una solicitud
     * concreta; lo de una fila se pregunta con `accionEnVueloSobre`, que es lo que no apaga de paso
     * los botones de las demás.
     */
    accionEnVuelo: function() {
      return cerrojosEnVuelo() > 0;
    }
  },
  methods: {
    /** Si hay una acción en vuelo sobre **eso**. */
    accionEnVueloSobre: function(clave) {
      return cerrojoPuesto(clave);
    },
    /** La clave de una solicitud, para la plantilla (ver {@link claveDeSolicitud}). */
    claveDeSolicitud: function(id) {
      return claveDeSolicitud(id);
    }
  }
};

/**
 * Coge el cerrojo de esa clave y devuelve **la forma de soltarlo**, que suelta una sola vez: el
 * camino bueno y el malo pueden llegar los dos, y una suelta de más abriría el cerrojo de la acción
 * siguiente.
 *
 * Lo llama `accionDeLaSesion` al salir la petición, no al contestar: el hueco que había entre una
 * cosa y otra es el que dejaba mandar la misma resolución dos veces.
 */
export function marcarEnVuelo(clave) {
  const testigo = cogerCerrojo(clave);
  return function() {
    soltarCerrojo(testigo);
  };
}
